import { UserEditRequest, UserWithSeat } from 'src/api';

export interface UserAddOnAssignment {
  meta?: Record<string, unknown> | null;
  add_ons?: { add_on_price_id?: string | null }[] | null;
}

function addOnPriceIdsFromMeta(
  user: UserAddOnAssignment,
  metaKey: 'add_ons' | 'invited_add_ons',
): string[] {
  const metaAddOns = user.meta?.[metaKey] as { price_id: string; quantity: number }[] | undefined;
  if (!Array.isArray(metaAddOns)) {
    return [];
  }

  const priceIds: string[] = [];
  for (const addon of metaAddOns) {
    const quantity = addon.quantity ?? 0;
    if (quantity <= 0 || !addon.price_id) {
      continue;
    }
    for (let i = 0; i < quantity; i++) {
      priceIds.push(addon.price_id);
    }
  }
  return priceIds;
}

export function userAddOnPriceIds(user: UserAddOnAssignment | undefined | null): string[] {
  if (!user) {
    return [];
  }

  if (Array.isArray(user.meta?.['add_ons'])) {
    return addOnPriceIdsFromMeta(user, 'add_ons');
  }

  return (user.add_ons ?? [])
    .map((addon) => addon.add_on_price_id)
    .filter((id): id is string => typeof id === 'string' && id.length > 0);
}


export function userHasAddOnPriceId(
  user: UserAddOnAssignment | undefined | null,
  priceId: string | undefined | null,
): boolean {
  if (!priceId) {
    return false;
  }
  return userAddOnPriceIds(user).includes(priceId);
}

export function firmHasAddOnPriceId(
  users: readonly UserAddOnAssignment[],
  priceId: string | undefined | null,
): boolean {
  if (!priceId) {
    return false;
  }
  return users.some((user) => userHasAddOnPriceId(user, priceId));
}

/**
 * Which of `priceIds` the firm actually holds the add-on at, or undefined when it holds none.
 *
 * A product is repriced by archiving its price and adding a new one, so the price a firm bought the
 * add-on at is often no longer the price it would be booked at today. The first match wins; a firm
 * part-way through a reprice holds both, and either answers "what this add-on costs us".
 */
export function firmHeldAddOnPriceId(
  users: readonly UserAddOnAssignment[],
  priceIds: readonly (string | undefined | null)[] | undefined | null,
): string | undefined {
  const ids = (priceIds ?? []).filter((id): id is string => !!id);
  if (ids.length === 0) {
    return undefined;
  }
  for (const user of users) {
    const held = userAddOnPriceIds(user).find((id) => ids.includes(id));
    if (held) {
      return held;
    }
  }
  return undefined;
}

/** Whether the firm holds an add-on at *any* of the prices that product was ever sold at. */
export function firmHasAddOnForPriceIds(
  users: readonly UserAddOnAssignment[],
  priceIds: readonly (string | undefined | null)[] | undefined | null,
): boolean {
  return !!firmHeldAddOnPriceId(users, priceIds);
}

/** Add-on rows the user is billed for today; an unbooking keeps them until the period ends. */
function heldAddOnPriceIds(user: UserAddOnAssignment): string[] {
  return (user.add_ons ?? [])
    .map((addon) => addon.add_on_price_id)
    .filter((id): id is string => typeof id === 'string' && id.length > 0);
}

/** Whether the firm may still use the add-on: booked now, or unbooked but paid until the period ends. */
export function firmHasAddOnEntitlementForPriceIds(
  users: readonly UserAddOnAssignment[],
  priceIds: readonly (string | undefined | null)[] | undefined | null,
): boolean {
  return firmHasAddOnForPriceIds(users, priceIds) || firmHeldAddOnUntilPeriodEnd(users, priceIds);
}

/** Unbooked (no longer desired) but still held until the current billing period ends. */
export function firmHeldAddOnUntilPeriodEnd(
  users: readonly UserAddOnAssignment[],
  priceIds: readonly (string | undefined | null)[] | undefined | null,
): boolean {
  const ids = (priceIds ?? []).filter((id): id is string => !!id);
  if (ids.length === 0 || firmHasAddOnForPriceIds(users, ids)) {
    return false;
  }
  return users.some((user) => heldAddOnPriceIds(user).some((id) => ids.includes(id)));
}

function isPendingUser(user: UserWithSeat): boolean {
  return !user.first_name && !user.last_name;
}

function seatPriceIdForUser(user: UserWithSeat): string | undefined {
  const meta = user.meta ?? {};

  // Legacy: invitations sent before invited seats were billed up front used their own meta keys.
  if (isPendingUser(user) && 'invited_seat_id' in meta) {
    return (meta['invited_seat_id'] as string | null | undefined) ?? undefined;
  }

  // A present-but-null desired seat means FREE (or a scheduled downgrade to FREE); only users
  // without any recorded seat change fall back to the seat they are billed for today.
  if ('desired_seat_price_id' in meta) {
    return (meta['desired_seat_price_id'] as string | null | undefined) ?? undefined;
  }

  return user.billing_price_id ?? undefined;
}

function toggleAddOnQuantities(
  currentIds: string[],
  priceId: string,
  enabled: boolean,
  couponCode?: string,
): { price_id: string; quantity: number; coupon_code?: string }[] {
  const withoutTarget = currentIds.filter((id) => id !== priceId);
  const nextIds = enabled ? [...withoutTarget, priceId] : withoutTarget;
  const counts = new Map<string, number>();
  for (const id of nextIds) {
    counts.set(id, (counts.get(id) ?? 0) + 1);
  }
  return [...counts.entries()].map(([id, quantity]) => ({
    price_id: id,
    quantity,
    ...(couponCode && id === priceId ? { coupon_code: couponCode } : {}),
  }));
}

export function buildTeamManagementAddOnRequests(
  users: readonly UserWithSeat[],
  priceId: string,
  enabled: boolean,
  couponCode?: string,
): UserEditRequest[] {
  return users
    .filter((user) => !!seatPriceIdForUser(user))
    .map((user) => {
      const currentIds = isPendingUser(user) && Array.isArray(user.meta?.['invited_add_ons'])
        ? addOnPriceIdsFromMeta(user, 'invited_add_ons')
        : userAddOnPriceIds(user);
      const seatPriceId = seatPriceIdForUser(user);
      return {
        user_id: user.id ?? '',
        role: user.role ?? UserEditRequest.RoleEnum.User,
        // Omitted seat_price_id means FREE on the backend — keep current seat on add-on-only edits.
        ...(seatPriceId ? { seat_price_id: seatPriceId } : {}),
        add_ons: toggleAddOnQuantities(currentIds, priceId, enabled, couponCode),
      };
    })
    .filter((entry) => !!entry.user_id);
}
